// ================================================================
// 認證 API 模組
// ================================================================
// 版本：1.0
// 日期：2026-01-14
// 功能：註冊、登入、Google OAuth、使用者資訊查詢
// ================================================================

import apiClient, { setAuthToken, removeAuthToken } from './client';

// ================================================================
// 型別定義
// ================================================================

export interface RegisterRequest {
  email: string;
  password: string;
  full_name: string;
  phone_number: string;
  birth_time: string;  // YYYYMMDDhh
  gender: 'Male' | 'Female';
  secret_code?: string;
  verify_token: string;  // 手機 OTP 驗證後取得的一次性 token
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface GoogleAuthRequest {
  credential: string;  // Google OAuth credential
}

export interface User {
  user_id: number;
  email: string;
  full_name: string;
  phone_number: string;
  birth_time: string;
  gender: 'Male' | 'Female';
  secret_code?: string;  // 專屬密碼（4碼數字）
  balance: number;
  bonus_points?: number;
  bonus_points_expires_at?: string;
  is_email_verified?: number;  // 0=未驗證, 1=已驗證
  available_balance?: number;  // 可用總額（餘額 + 點數）
  total_spent: number;
  debt_limit: number;
  preferred_currency: string;
  is_active: boolean;
  is_suspended: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: User;
  message: string;
}

// ================================================================
// API 函數
// ================================================================

/**
 * 使用者註冊
 */
export const register = async (data: RegisterRequest): Promise<AuthResponse> => {
  try {
    const response = await apiClient.post<AuthResponse>('/api/auth/register', data) as any as AuthResponse;

    // 儲存 Token 和使用者資訊
    if (response.success && response.token) {
      setAuthToken(response.token);
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }
    }

    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || '註冊失敗');
  }
};

/**
 * 使用者登入
 */
export const login = async (data: LoginRequest): Promise<AuthResponse> => {
  try {
    const response = await apiClient.post<AuthResponse>('/api/auth/login', data) as any as AuthResponse;

    // 儲存 Token 和使用者資訊
    if (response.success && response.token) {
      setAuthToken(response.token);
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }
    }

    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || '登入失敗');
  }
};

/**
 * Google OAuth 登入
 */
export const googleAuth = async (data: GoogleAuthRequest): Promise<AuthResponse> => {
  try {
    const response = await apiClient.post<AuthResponse>('/api/auth/google', data) as any as AuthResponse;

    // 儲存 Token 和使用者資訊
    if (response.success && response.token) {
      setAuthToken(response.token);
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }
    }

    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Google 登入失敗');
  }
};

/**
 * 清除認證 Token (別名)
 */
export const clearAuthToken = removeAuthToken;

/**
 * 登出
 */
export const logout = (): void => {
  removeAuthToken();
  window.location.href = '/login';
};

/**
 * 取得當前使用者資訊
 * @param firebaseIdToken - 可選的 Firebase ID Token (用於 Firebase 認證)
 */
export const getCurrentUser = async (firebaseIdToken?: string): Promise<{success: boolean; user?: User; message?: string}> => {
  try {
    // 如果提供了 Firebase ID Token，使用 POST 請求並傳送 token
    if (firebaseIdToken) {
      const response = await apiClient.post<{ success: boolean; user: User }>(
        '/api/auth/firebase/verify',
        { id_token: firebaseIdToken }
      ) as any as { success: boolean; user: User };

      if (response.success && response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        // 設置 Firebase ID Token 作為認證 token
        setAuthToken(firebaseIdToken);
      }

      return response;
    }

    // 否則使用 JWT 方式（從 Authorization header）
    const response = await apiClient.get<{ success: boolean; user: User }>('/api/auth/me') as any as { success: boolean; user: User };

    if (response.success && response.user) {
      // 更新 localStorage 中的使用者資訊
      localStorage.setItem('user', JSON.stringify(response.user));
    }

    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || '取得使用者資訊失敗');
  }
};

/**
 * 使用者修改密碼
 */
export const changeUserPassword = async (data: { current_password: string; new_password: string }): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/api/user/change-password', data
    ) as any as { success: boolean; message: string };
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || '密碼修改失敗');
  }
};

/**
 * 更新使用者偏好貨幣
 */
export const updatePreferredCurrency = async (currency: string): Promise<void> => {
  try {
    await apiClient.put('/api/user/settings/currency', { currency });
  } catch (error: any) {
    throw new Error(error.response?.data?.message || '更新貨幣設定失敗');
  }
};

/**
 * 驗證生辰八字格式
 */
export const validateBirthTime = (birthTime: string): boolean => {
  // 格式: YYYYMMDDhh (10位數字)
  if (!/^\d{10}$/.test(birthTime)) {
    return false;
  }

  const year = parseInt(birthTime.substring(0, 4));
  const month = parseInt(birthTime.substring(4, 6));
  const day = parseInt(birthTime.substring(6, 8));
  const hour = parseInt(birthTime.substring(8, 10));

  // 年份: 1901-2050
  if (year < 1901 || year > 2050) {
    return false;
  }

  // 月份: 01-12
  if (month < 1 || month > 12) {
    return false;
  }

  // 日期: 01-31
  if (day < 1 || day > 31) {
    return false;
  }

  // 時辰: 單數時辰 (01, 03, 05, ..., 23)
  const validHours = [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23];
  if (!validHours.includes(hour)) {
    return false;
  }

  return true;
};

/**
 * 格式化生辰八字顯示
 */
export const formatBirthTime = (birthTime: string): string => {
  if (birthTime.length !== 10) {
    return birthTime;
  }

  const year = birthTime.substring(0, 4);
  const month = birthTime.substring(4, 6);
  const day = birthTime.substring(6, 8);
  const hour = birthTime.substring(8, 10);

  return `${year}年${month}月${day}日 ${hour}時`;
};

/**
 * 取得時辰選項列表
 */
export const getHourOptions = (): Array<{ value: string; label: string }> => {
  return [
    { value: '23', label: '子時 (23:00-01:00)' },
    { value: '01', label: '丑時 (01:00-03:00)' },
    { value: '03', label: '寅時 (03:00-05:00)' },
    { value: '05', label: '卯時 (05:00-07:00)' },
    { value: '07', label: '辰時 (07:00-09:00)' },
    { value: '09', label: '巳時 (09:00-11:00)' },
    { value: '11', label: '午時 (11:00-13:00)' },
    { value: '13', label: '未時 (13:00-15:00)' },
    { value: '15', label: '申時 (15:00-17:00)' },
    { value: '17', label: '酉時 (17:00-19:00)' },
    { value: '19', label: '戌時 (19:00-21:00)' },
    { value: '21', label: '亥時 (21:00-23:00)' },
  ];
};

/**
 * Email 驗證完成後領取體驗點數
 */
export const activateEmailBonus = async (
  firebaseIdToken: string
): Promise<{ success: boolean; message: string; bonus_points?: number }> => {
  try {
    const response = await apiClient.post('/api/user/activate-bonus', {
      firebase_id_token: firebaseIdToken,
    }) as any;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || '領取失敗，請稍後再試');
  }
};
